<template>
    <modal title="Two Factor Authentication" width="50" :showModal="showModal" @close-modal="$emit('close-modal')">
      <div class="flex flex-col" v-loading="loading">
        <p class="my-4">
          Turn on two factor authentication for added protection to your account.
          As the root admin, you can also enforce this across your organization,
          thus every user has it turned on by default.
        </p>
        <div class="flex flex-col my-2">
          <label for="current">Current Password</label>
          <base-input type="password" placeholder="Enter Your Password" v-model="password">
            <template v-slot:icon>
              <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.45118 10.8262V12.7355M1.58551 7.64408V17.1905H15.3169V7.64408H1.58551ZM4.08212 7.64408C4.08212 3.8255 4.08212 1.27979 8.45118 1.27979C12.8202 1.27979 12.8202 3.8255 12.8202 7.64408H4.08212Z"
                  stroke="#FCC210" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </template>
          </base-input>
        </div>
  
        <base-button width="100" text="Confirm" :loading="confirmLoading" @click="confirmPassword">
        </base-button>
        <div :class="confirmed ? 'opacity-100' : 'opacity-50'">
          <div class="flex gap-2 my-4">
            <el-switch v-model="twoFA" active-color="#FCC210" inactive-color="#EEEEEE" @click="clicked2fa"
              :disabled="!confirmed" />
            2FA {{ twoFA ? "Enabled" : "Disabled" }}
          </div>
         
          <hr class="my-4" />
          <div class="" v-if="tokenSent">
            <h3 class="text-base font-semibold text-primary-yellow mb-3">
              Sent Authentication Code
            </h3>
            <p>Enter the one time password sent to your email: <span class="text-blue font-semibold"> {{ email }} </span>
            </p>
  
            <div class="authentication-code-area mx-auto sm:w-full">
              <div class="single-code">
                <input type="text" class="single-code--input no-outline" maxlength="1" pattern="[a-zA-Z0-9]*"
                  v-model="authenticationCode.first" @keyup="changeInputField" @paste="pasteCode($event)"
                  :disabled="!confirmed" />
             
                <input type="text" class="single-code--input no-outline" maxlength="1" pattern="[a-zA-Z0-9]*"
                  v-model="authenticationCode.second" @keyup="changeInputField" :disabled="!confirmed" />
                <input type="text" class="single-code--input no-outline" maxlength="1" pattern="[a-zA-Z0-9]*"
                  v-model="authenticationCode.third" @keyup="changeInputField" :disabled="!confirmed" />
                <input type="text" class="single-code--input no-outline" maxlength="1" pattern="[a-zA-Z0-9]*"
                  v-model="authenticationCode.fourth" @keyup="changeInputField" :disabled="!confirmed" />
                <input type="text" class="single-code--input no-outline" maxlength="1" pattern="[a-zA-Z0-9]*"
                  v-model="authenticationCode.fifth" @keyup="changeInputField" :disabled="!confirmed" />
                <input type="text" class="single-code--input no-outline" maxlength="1" pattern="[a-zA-Z0-9]*"
                  v-model="authenticationCode.sixth" @keyup="changeInputField" :disabled="!confirmed" />
              </div>
              <div class="verification-status" v-if="verified">
                <div class="verification successful" v-if="successfullyVerified">
                  <img src="@/assets/images/checkmark.png" alt="checkmark" class="img-fluid" />
                </div>
                <div class="verification failed" v-else>
                  <img src="@/assets/images/cancel.png" alt="checkmark" class="img-fluid" />
                </div>
              </div>
            </div>
  
            <p class="text-primary-yellow my-2 cursor-pointer" @click="sendToken">Resend Code</p>
          </div>
  
          <div class="flex items-center justify-between">
            <div class="cursor-pointer" @click="$router.go(-1)">
              <p>Cancel</p>
            </div>
            <base-button text="Save" @click="enableTwoFA" :disabled="!tokenSent" :loading="loading" />
          </div>
        </div>
      </div>
    </modal>
  </template>
  
  <script>
  import changeInputField from "@/mixins/changeInput";
  export default {
    props: {
      showModal: {
        type: Boolean,
        required: true,
      },
    },
    mixins: [changeInputField],
    data() {
      return {
        loading: false,
        confirmLoading: false,
        tokenSent: false,
        confirmed: null,
        twoFA: false,
        password: "",
        authenticationCode: {
          first: "",
          second: "",
          third: "",
          fourth: "",
          fifth: "",
          sixth: "",
        },
        successfullyVerified: false,
        userUpdate: false,
        verified: false
      };
    },
    watch: {
      twoFA(val) {
    
        if (this.userUpdate) {
          this.updateTwoFA(val);
        }
      }
    },
    computed: {
      email() {
        return this.$store.state.authentication.user.email;
      },
      token() {
        return this.$store.state.authentication.quantumToken;
      },
      userId() {
        return this.$store.state.authentication.userId;
      },
    },
    methods: {
      async confirmPassword() {
        let requestObj = {
          userId: this.userId,
          password: this.password,
        }
        this.confirmLoading = true
        try {
          const response = await this.$_confirmPassword(requestObj);
          if (response.data.success) {
            this.confirmed = response.data.success
            this.getTwoFactorStatus()
  
            this.confirmLoading = false
            this.userUpdate = true
  
    
          } else {
            this.generateToast(response.data.error, "error");
            this.confirmLoading = false
          }
        } catch (error) {
          this.confirmLoading = false
          this.handleError(error)
        }
      },
     
      async getTwoFactorStatus() {
        this.loading = true
        try {
          const response = await this.$_getTwoFactorStatus(this.userId);
          this.loading = false
          this.twoFA = response.data.data.isSetup;
  
        } catch (error) {
          this.loading = false
          this.handleError(error)
        }
      },
      async sendToken() {
        let requestObj = {
          userId: this.userId,
          password: this.password
        }
        this.loading = true
        try {
          await this.$_sendToken(requestObj);
          this.tokenSent = true;
          this.loading = false
          this.generateToast("Token has been resent successfully to your mail", "success");
  
        } catch (error) {
          this.loading = false
          this.handleError(error)
        }
      },
      async enableTwoFA() {
        let code = this.convertAuthCodeToNumbers();
  
        let requestObj = {
          userId: this.userId,
          token: code
        }
        this.loading = true
        try {
          
          await this.$_enableTwoFA(requestObj);
          this.loading = false
          this.resetState();
        
          this.generateToast("Two factor Auth enabled successfully", "success");
  
          this.$emit('close-modal')
          
        } catch (error) {
          this.loading = false
          this.handleError(error)
        }
      },
      resetState() {
        this.password = ''
        this.confirmed = null
        this.tokenSent = false
        this.authenticationCode = {
          first: "",
          second: "",
          third: "",
          fourth: "",
          fifth: "",
          sixth: "",
        }
  
        
      },
      async updateTwoFA(status) {
        let requestObj = {
          userId: this.userId,
          password: this.password,
        }
        this.loading = true
        try {
          if (status) {
        
            await this.sendToken(requestObj);
            this.generateToast("Token sent successfully to your email", "success");
          } else {
            await this.$_disableTwoFA(requestObj);
            this.generateToast("2FA disabled", "success");
            this.$emit('close-modal')
  
          }
          this.loading = false
        } catch (error) {
          this.loading = false
          this.handleError(error)
        }
      }, convertAuthCodeToNumbers() {
        let { first, second, third, fourth, fifth, sixth } = this.authenticationCode;
        let authCodeNumbers = first + second + third + fourth + fifth + sixth;
        return authCodeNumbers;
      },
      pasteCode(e) {
        e.preventDefault();
        let code = e.clipboardData.getData("text/plain");
        console.log("Code:", code);
        if (code.length == 6) {
          this.authenticationCode = {
            first: code.charAt(0),
            second: code.charAt(1),
            third: code.charAt(2),
            fourth: code.charAt(3),
            fifth: code.charAt(4),
            sixth: code.charAt(5),
          };
        } else {
          this.generateToast("Invalid code entered!", "error");
          this.authenticationCode.first = "";
        }
      },
      clicked2fa() {
        alert("Please enter ");
        this.userUpdate = true
      }
    },
    mounted() {
      this.getTwoFactorStatus()
    }
  
  };
  </script>
  
  <style scoped>
  .button {
    background: #fcc210;
    color: var(--primaryblue);
    width: 40%;
    font-weight: 500;
  
  }
  
  .new-address-button {
    text-decoration-line: underline;
  
    color: #da9601;
  }
  
  .radio-label span::after {
    background: #bec4d5;
  }
  </style>
  
  <style scoped>
  .authentication-screen-wrapper {
    width: 90%;
    margin: 0 auto;
  }
  
  .auth-code-form {
    width: 100%;
  }
  
  .single-code--input {
    width: 3.5rem;
    height: 3rem;
    background: #ffffff;
    border: 1px solid rgba(82, 82, 82, 0.5);
    box-sizing: border-box;
    border-radius: 8px;
    font-size: 1rem;
    margin-right: 1rem;
  }
  
  .single-code--input[type="text"] {
    font-size: 2.3rem;
    text-align: center;
    font-weight: bold;
    color: rgba(82, 82, 82, 0.3);
  }
  
  .authentication-code-area {
    display: flex;
    margin-top: 2rem;
  }
  
  .verify-btn-container {
    display: flex;
    margin: 2rem 0;
  }
  
  .create-password-container {
    margin-top: 3rem;
  }
  
  .verification {
    display: flex;
    height: 100%;
    align-items: center;
  }
  
  .cursor-pointer {
    cursor: pointer;
  }
  
  .lds-ellipsis div {
    background: #0253b3;
  }
  
  .loading-area {
    display: flex;
    justify-content: center;
    margin: 3rem 0 0.5rem 0;
  }
  
  @media screen and (max-width: 768px) {
    .single-code--input {
      width: 2rem;
      height: 2rem;
    }
  
    .single-code--input[type="text"] {
      font-size: 1.5rem;
    }
  
    .authentication-screen-wrapper {
      width: 100%;
      margin: 0 auto;
    }
  }
  </style>
  